import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService, ContentService, LanguageService } from "@mypxplat/xplat/core";
import { EmbedBridgeService } from "@mypxplat/xplat/web/core";
import { takeUntil } from "rxjs/operators";

/**
 * Chrome-less livestream embed for external sites (fender.com, later play.fender.com).
 * Rendered inside an <iframe>; fills its container. Two modes:
 *  - `destination`: shows the next-upcoming livestream + countdown, swaps to the inline player when live.
 *  - `videoId` (a Learn video id): skips the event flow and plays that Learn video (Vimeo or YouTube),
 *    muted-autoplay, so any video has its own paste-anywhere embed code.
 * `hideComments` force-hides comments in either mode. Comments are shared with the my.fender player
 * via the same {platform}_{platform_id} thread key.
 *
 * The pre-show countdown itself lives in `fui-livestream-countdown`, shared with the in-app video
 * player; this component only decides between it, the player, and the empty/loading states.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-embed-livestream",
  templateUrl: "fui-embed-livestream.component.html",
  standalone: false,
})
export class FuiEmbedLivestreamComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() destination: string = "fender.com";
  /** Learn video id — when set, embed plays that video directly (no event/countdown). */
  @Input() videoId: string = "";
  /** Force-hide comments, overriding the event's comments_enabled setting. */
  @Input() hideComments: boolean = false;
  /** Start offset like "13s" / "1m30s" / "90" — applied to YouTube playback (?t=). */
  @Input() startTime: string = "";
  /** DEMO: seed the shop rail with sample products so the UI can be pruned before the host plumbing. */
  @Input() shopDemo: boolean = false;
  /** base64url-encoded shop payload from the URL (?shop=) — for hosts that can't run JS. */
  @Input() shopData: string = "";
  /**
   * Per-event panel features (DENG-893). URL-param overrides for local testing; the real values
   * come off the event (`shop_enabled` / `signup_enabled` / `signup_config`) once it loads.
   */
  @Input() shopEnabled: boolean = true;
  @Input() signupEnabled: boolean = false;
  /** Trailer/preview YouTube id (from `?previewVideo=`); shows a "Watch The Trailer" CTA on the countdown. */
  @Input() previewVideoId: string = "";
  /**
   * Viewer locale for the event title/description (from `?lang=`, host-provided). Blank → the app's
   * selected language, else English. Localization + English fallback happen server-side.
   */
  @Input() lang: string = "";

  public loading: boolean = true;
  public event: any = null;
  public playerVideo: any = null;
  public commentsEnabled: boolean = true;
  public isHappeningNow: boolean = false;

  /** Resolved feature flags handed to the player / countdown (event value, else the URL override). */
  public shopEnabledResolved: boolean = true;
  public signupEnabledResolved: boolean = false;
  /** Per-state sign-up config off the event ({ countdown, livestream }), or null. */
  public signupConfigResolved: any = null;

  constructor(
    userService: UserService,
    public eventsService: EventsService,
    private contentService: ContentService,
    private embedBridge: EmbedBridgeService,
    private languageService: LanguageService
  ) {
    super(userService);
  }

  /**
   * Viewer locale for event content, in precedence order:
   *  1. explicit `?lang=` (host-provided — fender.com knows best),
   *  2. a signed-in viewer's saved account language,
   *  3. the browser's preferred language (best guess for anonymous viewers), mapped to a code we
   *     support, then
   *  4. English.
   * The server does the actual localization + English fallback given whatever we send.
   */
  private resolveLang(): string {
    if (this.lang) return this.lang;
    const userLang = (this.user as any)?.language_code;
    if (userLang) return userLang;
    const prefs = typeof navigator !== "undefined" ? (navigator.languages?.length ? Array.from(navigator.languages) : [navigator.language]) : [];
    return (
      FuiEmbedLivestreamComponent.resolveSupportedLang(
        prefs,
        this.languageService.languages.map((l) => l.code)
      ) || "en"
    );
  }

  /**
   * First browser-preferred language we support. Browser codes are BCP-47 ("fr-FR", "ja", "zh-Hant"),
   * so we match on the primary subtag and alias the two app codes that differ from the standard
   * (Japanese ja→jp, Chinese zh→cn). Pure/static so it's trivially testable.
   */
  static resolveSupportedLang(prefs: string[], supported: string[]): string | null {
    const set = new Set(supported);
    const alias: Record<string, string> = { ja: "jp", zh: "cn" };
    for (const raw of prefs || []) {
      if (!raw) continue;
      const primary = String(raw).toLowerCase().split("-")[0];
      const code = alias[primary] || primary;
      if (set.has(code)) return code;
    }
    return null;
  }

  ngOnInit() {
    // Open the postMessage bridge so the host page (fender.com) can push in the shopping rail.
    // Lives for the whole embed session so data survives the countdown→live handoff; the player
    // reads it off EmbedBridgeService.shop$ once it mounts.
    this.embedBridge.start();
    // Shop rail from the URL (?shop=) — for host pages that can't run a postMessage script.
    if (this.shopData) this.embedBridge.loadFromEncoded(this.shopData);
    // DEMO: seed sample products so the shop UI can be pruned before the host plumbing exists.
    if (this.shopDemo) this.embedBridge.seedDemo();

    // Warm the country list on page load (it's cached) so the sign-up modal's country picker is
    // populated the instant it opens, rather than firing the request on first interaction.
    this.userService
      .listCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => {} });

    // Seed resolved feature flags from the inputs (URL overrides); the event path below refines them.
    this.shopEnabledResolved = this.shopEnabled;
    this.signupEnabledResolved = this.signupEnabled;

    // "Embed this video" mode: play a specific Learn video directly, no event/countdown.
    if (this.videoId) {
      this.loadLearnVideo();
      return;
    }

    this.eventsService.getEmbedLivestream(this.destination, this.resolveLang()).subscribe({
      next: (result) => {
        this.event = result.event;
        this.loading = false;
        if (this.event) {
          this.commentsEnabled = this.hideComments ? false : this.event.comments_enabled !== false;
          // Per-event feature flags (admin config). Shop/sign-up default OFF unless enabled for the
          // event; a URL override can still force sign-up on for testing.
          this.shopEnabledResolved = this.event.shop_enabled === true;
          this.signupEnabledResolved = this.event.signup_enabled === true || this.signupEnabled;
          this.signupConfigResolved = this.event.signup_config || null;
          // Already in progress on load: skip the countdown entirely. Otherwise the countdown
          // component renders and tells us when to swap in the player.
          if (this.eventsService.getEventTimingState(this.event) === "live") this.onWentLive();
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Countdown hit zero (or the event was already live on load) — mount the player. */
  onWentLive() {
    this.loadPlayer();
    this.isHappeningNow = true;
  }

  /**
   * Build the video descriptor the embedded player expects, keyed to share the comment thread.
   * Comment thread key is `{platform}_{videoId}` (see video-player.setupComments), which stays
   * unique across platforms — YT and Vimeo can never collide even with the same numeric id.
   */
  private loadPlayer() {
    if (this.playerVideo) return;
    const platform = this.eventsService.getLivestreamPlatform(this.event);
    const videoId = this.eventsService.getLivestreamVideoId(this.event);
    if (!platform || !videoId) return;
    this.playerVideo = {
      id: videoId,
      type: platform,
      title: this.event.title,
      thumb: this.event.image_url,
      isLiveEvent: true,
      // Intentionally NOT muted. The viewer sat through the countdown; forcing them to unmute
      // when the stream mounts is an extra unnecessary step. Browsers may still block unmuted
      // autoplay without user activation — in that case the player shows its own play button,
      // which is better UX than starting silent.
    };
  }

  /**
   * "Embed this video" mode: resolve a Learn video by id and play it directly (muted-autoplay).
   * Passing `presonusVideo` inline lets the player apply subscription_only gating + description
   * without the authed content catalog. Comments key off {platform}_{platform_id}, shared with
   * the main site.
   */
  private loadLearnVideo() {
    this.commentsEnabled = !this.hideComments;
    this.contentService.getPublicLearnVideo(this.videoId).subscribe({
      next: (video: any) => {
        this.loading = false;
        if (!video || !video.platform_id) return;
        this.playerVideo = {
          id: video.platform_id,
          type: video.platform,
          title: video.title,
          thumb: video.image,
          presonus_id: video.id,
          presonusVideo: video,
          muted: true,
          start: this.parseStartSeconds(this.startTime),
        };
        this.isHappeningNow = true;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.embedBridge.stop();
    super.ngOnDestroy();
  }

  /** Parse a start offset like "13s", "1m30s", "1h2m3s", or a plain "90" into seconds. */
  private parseStartSeconds(t: string): number {
    if (!t) return 0;
    const hms = t.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (hms && (hms[1] || hms[2] || hms[3])) {
      return +(hms[1] || 0) * 3600 + +(hms[2] || 0) * 60 + +(hms[3] || 0);
    }
    const n = parseInt(t, 10);
    return isNaN(n) ? 0 : n;
  }
}
